import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';

const tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';

const TimetableDataViewer = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [filters, setFilters] = useState({
    teacher_id: '',
    teacher_name: '',
    day_of_week: '',
    subject: '',
    class_id: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [limit, setLimit] = useState(100);

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const loadTimetableData = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('Loading timetable data...', 'info');

      let query = supabase
        .from('saas_teacher_timetables')
        .select('*')
        .eq('tenant_id', tenantId);

      // Apply filters
      if (filters.teacher_id) {
        query = query.ilike('teacher_id', `%${filters.teacher_id}%`);
      }
      if (filters.teacher_name) {
        query = query.ilike('teacher_name', `%${filters.teacher_name}%`);
      }
      if (filters.day_of_week) {
        query = query.eq('day_of_week', filters.day_of_week);
      }
      if (filters.subject) {
        query = query.ilike('subject', `%${filters.subject}%`);
      }
      if (filters.class_id) {
        query = query.ilike('class_id', `%${filters.class_id}%`);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply limit
      query = query.limit(limit);

      const { data: result, error: queryError } = await query;

      if (queryError) {
        console.error('Error loading data:', queryError);
        setError(`Database Error: ${queryError.message}`);
        showMessage(`Error loading data: ${queryError.message}`, 'error');
        return;
      }

      // Validate and clean data
      const cleanedData = (result || []).map((record, index) => {
        // Debug: Log the raw record to see what we're getting
        console.log(`Record ${index}:`, record);
        console.log(`Record ID type:`, typeof record?.id, record?.id);
        
        // Ensure all required fields exist and are properly typed
        return {
          id: record.id || `temp_${index}`,
          teacher_id: record.teacher_id || '',
          teacher_name: record.teacher_name || '',
          day_of_week: record.day_of_week || '',
          time_slot: record.time_slot || '',
          period_number: record.period_number || 0,
          period_sequence: record.period_sequence || record.period_number || 0,
          period_node: record.period_node || `Period ${record.period_number || 0}`,
          subject: record.subject || '',
          class_id: record.class_id || '',
          classroom: record.classroom || '',
          week_type: record.week_type || '',
          subject_type: record.subject_type || '',
          elective_group: record.elective_group || '',
          notes: record.notes || '',
          created_at: record.created_at || '',
          updated_at: record.updated_at || ''
        };
      });

      console.log('Cleaned data:', cleanedData);
      setData(cleanedData);
      showMessage(`Loaded ${cleanedData.length} records successfully`, 'success');

    } catch (err) {
      console.error('Error loading timetable data:', err);
      setError(`Error: ${err.message}`);
      showMessage(`Error loading data: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      teacher_id: '',
      teacher_name: '',
      day_of_week: '',
      subject: '',
      class_id: ''
    });
  };

  const exportToCSV = () => {
    if (data.length === 0) {
      showMessage('No data to export', 'error');
      return;
    }

    const headers = [
      'ID', 'Teacher ID', 'Teacher Name', 'Day of Week', 'Time Slot', 
      'Period Number', 'Period Sequence', 'Period Node', 'Subject', 'Class ID', 'Classroom', 'Week Type', 
      'Subject Type', 'Elective Group', 'Notes', 'Created At', 'Updated At'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(record => [
        record.id,
        record.teacher_id || '',
        record.teacher_name || '',
        record.day_of_week || '',
        record.time_slot || '',
        record.period_number || '',
        record.period_sequence || '',
        record.period_node || '',
        `"${(record.subject || '').replace(/"/g, '""')}"`,
        record.class_id || '',
        record.classroom || '',
        record.week_type || '',
        record.subject_type || '',
        record.elective_group || '',
        `"${(record.notes || '').replace(/"/g, '""')}"`,
        record.created_at || '',
        record.updated_at || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timetable_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showMessage('Data exported to CSV successfully', 'success');
  };

  const getUniqueValues = (field) => {
    const values = data.map(record => record[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  useEffect(() => {
    loadTimetableData();
  }, []);

  return (
    <div className="timetable-data-viewer bg-white rounded-lg shadow-md p-6 mb-6" style={{maxWidth: '600px', width: '100%'}}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="mr-2">🔍</span>
        Timetable Data Viewer (Debug) - 半寬度版本
        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">600px</span>
      </h3>

      {/* Filters */}
      <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">Filters</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher ID</label>
            <input
              type="text"
              value={filters.teacher_id}
              onChange={(e) => setFilters(prev => ({ ...prev, teacher_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g., tn_01-t077"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teacher Name</label>
            <input
              type="text"
              value={filters.teacher_name}
              onChange={(e) => setFilters(prev => ({ ...prev, teacher_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g., 陳嘉俊"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
            <select
              value={filters.day_of_week}
              onChange={(e) => setFilters(prev => ({ ...prev, day_of_week: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Days</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={filters.subject}
              onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g., 英國語文"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class ID</label>
            <input
              type="text"
              value={filters.class_id}
              onChange={(e) => setFilters(prev => ({ ...prev, class_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="e.g., 4AB3"
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={loadTimetableData}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="created_at">Created At</option>
            <option value="teacher_name">Teacher Name</option>
            <option value="day_of_week">Day of Week</option>
            <option value="period_number">Period Number</option>
            <option value="subject">Subject</option>
            <option value="class_id">Class ID</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Limit:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded text-sm"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
        <button
          onClick={exportToCSV}
          disabled={data.length === 0}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
        >
          Export CSV
        </button>
      </div>

      {/* Data Summary */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-700">Total Records:</span>
            <span className="ml-2 text-blue-600">{data.length}</span>
          </div>
          <div>
            <span className="font-medium text-blue-700">Teachers:</span>
            <span className="ml-2 text-blue-600">{getUniqueValues('teacher_name').length}</span>
          </div>
          <div>
            <span className="font-medium text-blue-700">Subjects:</span>
            <span className="ml-2 text-blue-600">{getUniqueValues('subject').length}</span>
          </div>
          <div>
            <span className="font-medium text-blue-700">Classes:</span>
            <span className="ml-2 text-blue-600">{getUniqueValues('class_id').length}</span>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <span className="mr-2">❌</span>
            {error}
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {messageType === 'success' ? '✅' : messageType === 'error' ? '❌' : 'ℹ️'}
            </span>
            {message}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="overflow-x-auto" style={{maxWidth: '580px'}}>
        <table className="min-w-full border-collapse border border-gray-300" style={{fontSize: '0.75rem'}}>
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">ID</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Teacher ID</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Teacher Name</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Day</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Time Slot</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Period</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Period Seq</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Period Node</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Subject</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Class ID</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Classroom</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Week Type</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Subject Type</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Notes</th>
              <th className="border border-gray-300 px-1 py-1 text-xs font-medium">Created At</th>
            </tr>
          </thead>
          <tbody>
            {data.map((record, index) => (
              <tr key={record.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-1 py-1 text-xs">
                  {(() => {
                    try {
                      if (record.id && typeof record.id === 'string') {
                        return record.id.substring(0, 8) + '...';
                      } else if (record.id) {
                        return String(record.id).substring(0, 8) + '...';
                      }
                      return 'N/A';
                    } catch (e) {
                      console.error('Error displaying ID:', e, record);
                      return 'Error';
                    }
                  })()}
                </td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.teacher_id || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.teacher_name || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.day_of_week || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.time_slot || 'N/A'}</td>
                <td className="border border-gray-300 px-2 py-1 text-xs text-center">{record.period_number || 'N/A'}</td>
                <td className="border border-gray-300 px-2 py-1 text-xs text-center">{record.period_sequence || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.period_node || 'N/A'}</td>
                <td className="border border-gray-300 px-2 py-1 text-xs max-w-xs truncate">{record.subject || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.class_id || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.classroom || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.week_type || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">{record.subject_type || 'N/A'}</td>
                <td className="border border-gray-300 px-2 py-1 text-xs max-w-xs truncate">{record.notes || 'N/A'}</td>
                <td className="border border-gray-300 px-1 py-1 text-xs">
                  {record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          No data found. Try adjusting your filters or check if data exists in the database.
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading data...
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetableDataViewer;
